import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useHistory } from "react-router-dom";
import {
  Card,
  Col,
  Row,
  Typography,
  Table,
  Tag,
  Avatar,
  Button,
  List,
  Space,
  Badge,
  Tooltip,
  Grid,
  Divider,
  message,
  Modal,
  Input,
  Select,
  Form
} from "antd";
import {
  UserOutlined,
  ProjectOutlined,
  WarningOutlined,
  WifiOutlined,
  ReloadOutlined,
  ExportOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  ThunderboltOutlined,
  DashboardOutlined,
  VideoCameraOutlined,
  FileTextOutlined,
  CalendarOutlined,
  BugOutlined,
  EnvironmentOutlined,
  DesktopOutlined,
  PlusOutlined
} from "@ant-design/icons";

if (typeof window !== "undefined" && !window.process) {
  window.process = { env: {} };
}

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;
const { Option } = Select;

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://127.0.0.1:8000";
const API_BASE = `${API_BASE_URL}/vault_backend`;

const formatDuration = (seconds) => {
  if (!seconds && seconds !== 0) return "-";
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  return `${hrs.toString().padStart(2, "0")}:${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
};

// ---------- Chart Components ----------

const TimelineChart = ({ timeline, screens = {} }) => {
  const maxCount = Math.max(...timeline.map(t => t.count), 1);
  const peakDay = timeline.find(day => day.count === maxCount);

  const chartMinHeight = screens.xs ? 200 : undefined;
  const barAreaMinHeight = screens.xs ? 120 : undefined;

  return (
    <div style={{
      display: "flex",
      flexDirection: "column",
      flex: 1,
      minHeight: chartMinHeight || 0,
      paddingTop: 12,
    }}>
      {peakDay && maxCount > 0 && (
        <div style={{ marginBottom: 16, textAlign: "center", flexShrink: 0 }}>
          <Tag color="gold" icon={<ArrowUpOutlined />} style={{ borderRadius: 20, fontWeight: 600 }}>
            Peak: {peakDay.label} ({peakDay.count} errors)
          </Tag>
        </div>
      )}
      <div style={{
        flex: 1,
        display: "flex",
        alignItems: "flex-end",
        gap: 16,
        minHeight: barAreaMinHeight || 0,
      }}>
        {timeline.map((item, idx) => {
          const barHeightPercent = maxCount > 0 ? (item.count / maxCount) * 100 : 0;
          const isPeak = item.count === maxCount;
          return (
            <div key={idx} style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 10,
              height: "100%",
            }}>
              <div style={{
                flex: 1,
                width: "100%",
                display: "flex",
                alignItems: "flex-end",
              }}>
                <Tooltip title={`${item.count} errors on ${item.label}`}>
                  <div
                    style={{
                      height: `${barHeightPercent}%`,
                      width: "100%",
                      background: isPeak
                        ? "linear-gradient(180deg, #ff7a6e 0%, #ff3b30 100%)"
                        : "linear-gradient(180deg, #ff9b8f 0%, #ff6b6b 100%)",
                      borderRadius: "10px 10px 4px 4px",
                      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                      cursor: "pointer",
                      position: "relative",
                      boxShadow: isPeak
                        ? "0 0 12px rgba(255,59,48,0.4), 0 4px 12px rgba(0,0,0,0.08)"
                        : "0 4px 10px rgba(0,0,0,0.05)",
                      minHeight: 4,
                    }}
                    className="timeline-bar"
                  >
                    <div style={{
                      position: "absolute",
                      top: 4,
                      left: 4,
                      right: 4,
                      height: "30%",
                      background: "linear-gradient(180deg, rgba(255,255,255,0.3) 0%, rgba(255,255,255,0) 100%)",
                      borderRadius: "8px 8px 0 0",
                    }} />
                  </div>
                </Tooltip>
              </div>
              <Text type="secondary" style={{ fontSize: 11, fontWeight: 500, flexShrink: 0 }}>{item.label}</Text>
              <Text strong style={{
                fontSize: 13,
                color: isPeak ? "#ff4d4f" : "#1f1f1f",
                flexShrink: 0,
                fontWeight: isPeak ? 700 : 600,
              }}>
                {item.count}
              </Text>
            </div>
          );
        })}
      </div>
      <div style={{ textAlign: "center", marginTop: 12, flexShrink: 0 }}>
        <Text type="secondary" style={{ fontSize: 12, letterSpacing: 0.5 }}>Errors per day (last 7 days)</Text>
      </div>
      <style>{`
        .timeline-bar:hover {
          transform: translateY(-6px);
          filter: brightness(1.05);
        }
      `}</style>
    </div>
  );
};

const TopErrorsChart = ({ data }) => {
  const maxCount = Math.max(...data.map(d => d.count), 1);
  return (
    <div style={{ padding: "8px 0", display: "flex", flexDirection: "column", height: "100%", justifyContent: "flex-start" }}>
      {data.map((item, idx) => (
        <div key={idx} style={{ marginBottom: 24, animation: `fadeInUp 0.3s ease ${idx * 0.05}s both` }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
            <Tooltip title={item.message}>
              <Text
                style={{
                  fontSize: 13,
                  fontWeight: 500,
                  color: "#1f1f1f",
                  flex: 1,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  marginRight: 8,
                  letterSpacing: -0.2,
                }}
                ellipsis
              >
                {item.message}
              </Text>
            </Tooltip>
            <Text strong style={{ color: "#1677ff", whiteSpace: "nowrap", fontSize: 14 }}>
              {item.count}
            </Text>
          </div>
          <div style={{ background: "#f0f2f5", borderRadius: 10, overflow: "hidden", boxShadow: "inset 0 1px 3px rgba(0,0,0,0.04)" }}>
            <div
              style={{
                width: `${(item.count / maxCount) * 100}%`,
                height: 34,
                background: "linear-gradient(135deg, #1677ff 0%, #5b8cff 100%)",
                borderRadius: 10,
                display: "flex",
                alignItems: "center",
                paddingLeft: 12,
                color: "white",
                fontWeight: 600,
                fontSize: 13,
                transition: "width 0.6s cubic-bezier(0.4, 0, 0.2, 1)",
                boxShadow: "0 2px 6px rgba(22,119,255,0.3)",
              }}
            >
              {((item.count / maxCount) * 100).toFixed(0)}%
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

// ---------- Main Component ----------
function Home() {
  const history = useHistory();
  const screens = useBreakpoint();
  const [loading, setLoading] = useState(false);

  const [metrics, setMetrics] = useState({
    totalUsers: 0,
    totalUsersTrend: 0,
    activeProjects: 0,
    activeProjectsNew: 0,
    openLogs: 0,
    liveSessions: 0
  });
  const [recentLogs, setRecentLogs] = useState([]);
  const [topErrors, setTopErrors] = useState([]);
  const [errorTimeline, setErrorTimeline] = useState([]);
  const [sessionSnapshot, setSessionSnapshot] = useState([]);
  const [projects, setProjects] = useState([]);

  const [isAddUserModalVisible, setIsAddUserModalVisible] = useState(false);
  const [isAddProjectModalVisible, setIsAddProjectModalVisible] = useState(false);
  const [newUser, setNewUser] = useState({
    username: "",
    email: "",
    role: "",
    projectId: "",
    projectName: ""
  });
  const [newProject, setNewProject] = useState({
    projectName: "",
    status: "active",
    remark: ""
  });

  // >>> ADDED: compute project name mapping from existing projects state
  const projectMap = useMemo(() => {
    const map = {};
    projects.forEach(p => {
      if (p.project_id && p.project_name) {
        map[p.project_id] = p.project_name;
      }
    });
    return map;
  }, [projects]);
  // <<<

  const fetchDashboardData = useCallback(async () => {
    setLoading(true);
    try {
      const metricsRes = await fetch(`${API_BASE}/dashboard/metrics`);
      if (!metricsRes.ok) throw new Error("Failed to fetch metrics");
      const metricsData = await metricsRes.json();
      setMetrics(metricsData);

      const logsRes = await fetch(`${API_BASE}/dashboard/recent-logs?limit=10`);
      if (!logsRes.ok) throw new Error("Failed to fetch recent logs");
      const logsData = await logsRes.json();
      setRecentLogs(logsData);

      const topErrorsRes = await fetch(`${API_BASE}/dashboard/top-errors?limit=5`);
      if (!topErrorsRes.ok) throw new Error("Failed to fetch top errors");
      const topErrorsData = await topErrorsRes.json();
      setTopErrors(topErrorsData);

      const timelineRes = await fetch(`${API_BASE}/dashboard/error-timeline?days=7`);
      if (!timelineRes.ok) throw new Error("Failed to fetch error timeline");
      const timelineData = await timelineRes.json();
      setErrorTimeline(timelineData);

      const sessionsRes = await fetch(`${API_BASE}/dashboard/sessions/snapshot?limit=10`);
      if (!sessionsRes.ok) throw new Error("Failed to fetch session snapshot");
      const sessionsData = await sessionsRes.json();
      const sorted = [...sessionsData].sort((a, b) => {
        if (a.status === "active" && b.status !== "active") return -1;
        if (a.status !== "active" && b.status === "active") return 1;
        return new Date(b.loginTime) - new Date(a.loginTime);
      });
      setSessionSnapshot(sorted.slice(0, 5));
    } catch (error) {
      console.error("Dashboard data fetch error:", error);
      message.error("Failed to load dashboard data. Please try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchProjects = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/projects/`);
      if (response.ok) {
        const result = await response.json();
        setProjects(result);
      }
    } catch (error) {
      console.error("Error fetching projects:", error);
    }
  }, []);

  useEffect(() => {
    fetchDashboardData();
    fetchProjects();
  }, [fetchDashboardData, fetchProjects]);

  const handleAddUser = async () => {
    if (!newUser.username || !newUser.email || !newUser.role || !newUser.projectId) {
      message.warning("Please fill all fields");
      return;
    }
    try {
      const requestBody = {
        username: newUser.username,
        email: newUser.email,
        role: newUser.role,
        project_id: newUser.projectId,
        project_name: newUser.projectName
      };
      const response = await fetch(`${API_BASE_URL}/users/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestBody)
      });
      if (!response.ok) throw new Error("Failed to add user");
      message.success("User added successfully!");
      setIsAddUserModalVisible(false);
      setNewUser({ username: "", email: "", role: "", projectId: "", projectName: "" });
      fetchDashboardData();
      fetchProjects();
    } catch (error) {
      console.error("Error adding user:", error);
      message.error("Failed to add user");
    }
  };

  const handleAddProject = async () => {
    if (!newProject.projectName) {
      message.warning("Please enter project name");
      return;
    }
    try {
      const requestBody = {
        project_name: newProject.projectName,
        status: newProject.status,
        remarks: newProject.remark
      };
      const response = await fetch(`${API_BASE_URL}/projects/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestBody)
      });
      if (!response.ok) throw new Error("Failed to add project");
      message.success("Project added successfully!");
      setIsAddProjectModalVisible(false);
      setNewProject({ projectName: "", status: "active", remark: "" });
      fetchDashboardData();
      fetchProjects();
    } catch (error) {
      console.error("Error adding project:", error);
      message.error("Failed to add project");
    }
  };

  const handleRefresh = () => fetchDashboardData();

  const logColumns = [
    {
      title: "Log ID",
      dataIndex: "logId",
      key: "logId",
      width: 90,
      render: (text) => <Text code style={{ fontSize: 12, fontWeight: 500 }}>{text}</Text>
    },
    {
      title: "Project",
      dataIndex: "projectId",
      key: "projectId",
      width: 110,
      // >>> MODIFIED: show project name instead of raw ID
      render: (text) => <Tag color="blue">{projectMap[text] || text}</Tag>
    },
    {
      title: "Error",
      dataIndex: "errorMessage",
      key: "errorMessage",
      width: 220,
      ellipsis: true,
      render: (text) => (
        <Tooltip title={text}>
          <Text style={{ fontSize: 13, color: "#d4380d" }} ellipsis>{text || ""}</Text>
        </Tooltip>
      )
    },
    {
      title: "Time",
      dataIndex: "timestamp",
      key: "timestamp",
      width: 170,
      render: (text) => {
        if (!text) return "-";
        const date = new Date(text);
        return <Text type="secondary" style={{ fontSize: 12 }}>{date.toLocaleString()}</Text>;
      }
    }
  ];

  const formatSessionTime = (isoString) => {
    if (!isoString) return "";
    const date = new Date(isoString);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };

  const quickActions = [
    {
      title: "Add User",
      icon: <UserOutlined />,
      description: "Create and assign project roles",
      color: "#1677ff",
      bg: "linear-gradient(135deg, #e6f4ff 0%, #d6e8ff 100%)",
      onClick: () => setIsAddUserModalVisible(true)
    },
    {
      title: "Add Project",
      icon: <ProjectOutlined />,
      description: "Create new project workspace",
      color: "#52c41a",
      bg: "linear-gradient(135deg, #f0fff0 0%, #d9f7d9 100%)",
      onClick: () => setIsAddProjectModalVisible(true)
    },
    {
      title: "Open Critical Logs",
      icon: <WarningOutlined />,
      description: "Review unresolved error logs",
      color: "#fa541c",
      bg: "linear-gradient(135deg, #fff2e8 0%, #ffe5d4 100%)",
      onClick: () => history.push("/logmanagement")
    },
    {
      title: "Track Live Sessions",
      icon: <WifiOutlined />,
      description: "Inspect active user sessions",
      color: "#722ed1",
      bg: "linear-gradient(135deg, #f9f0ff 0%, #efdbff 100%)",
      onClick: () => history.push("/sessionmanagement")
    }
  ];

  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = `
      @keyframes fadeInUp {
        from { opacity: 0; transform: translateY(20px); }
        to { opacity: 1; transform: translateY(0); }
      }
      .metric-card { animation: fadeInUp 0.6s ease forwards; opacity: 0; }
      .metric-card:nth-child(1) { animation-delay: 0.05s; }
      .metric-card:nth-child(2) { animation-delay: 0.1s; }
      .metric-card:nth-child(3) { animation-delay: 0.15s; }
      .metric-card:nth-child(4) { animation-delay: 0.2s; }
      .quick-action-btn:hover .action-icon { transform: scale(1.1); }
      .premium-row {
        transition: all 0.2s ease;
        border-radius: 12px;
      }
      .premium-row:hover {
        background: linear-gradient(90deg, #f8faff 0%, #f0f5ff 100%) !important;
        box-shadow: 0 4px 12px rgba(22,119,255,0.08);
        transform: translateY(-1px);
      }
      .ant-table-thead > tr > th {
        background: #f9fafb !important;
        font-weight: 600 !important;
        color: #1f1f1f !important;
        border-bottom: 2px solid #e8ecf1 !important;
      }
      .ant-table-tbody > tr > td {
        border-bottom: 1px solid #f0f2f5 !important;
      }

      /* ---------- Premium Modal Styling ---------- */
      .premium-modal .ant-modal-content {
        border-radius: 24px;
        background: rgba(255,255,255,0.85);
        backdrop-filter: blur(20px);
        box-shadow: 0 20px 60px rgba(0,0,0,0.12), 0 8px 20px rgba(0,0,0,0.08);
        border: 1px solid rgba(255,255,255,0.6);
        overflow: hidden;
      }
      .premium-modal .ant-modal-header {
        background: transparent;
        border-bottom: none;
        padding: 20px 24px 0;
      }
      .premium-modal .ant-modal-title {
        font-size: 20px;
        font-weight: 700;
        color: #1f1f1f;
        display: flex;
        align-items: center;
        gap: 8px;
      }
      .premium-modal .ant-modal-close {
        color: #8c8c8c;
        transition: color 0.2s;
      }
      .premium-modal .ant-modal-close:hover {
        color: #1f1f1f;
      }
      .premium-modal .ant-modal-body {
        padding: 20px 24px 16px;
      }
      .premium-modal .ant-modal-footer {
        border-top: none;
        padding: 12px 24px 20px;
      }
      .premium-modal .ant-form-item-label > label {
        font-weight: 600;
        color: #1f1f1f;
        fontSize: 13px;
        height: auto;
      }
      .premium-modal .ant-input,
      .premium-modal .ant-select-selector {
        border-radius: 12px !important;
        border: 1px solid #e8ecf1 !important;
        padding: 8px 14px !important;
        height: 44px !important;
        display: flex;
        align-items: center;
        transition: all 0.2s ease;
        background: white;
      }
      .premium-modal .ant-input:hover,
      .premium-modal .ant-select-selector:hover {
        border-color: #b3c0d1 !important;
      }
      .premium-modal .ant-input:focus,
      .premium-modal .ant-select-focused .ant-select-selector {
        border-color: #1677ff !important;
        box-shadow: 0 0 0 3px rgba(22,119,255,0.1) !important;
      }
      .premium-modal .ant-btn {
        border-radius: 12px;
        height: 44px;
        font-weight: 600;
        padding: 0 24px;
        transition: all 0.2s ease;
      }
      .premium-modal .ant-btn-default {
        background: #f5f5f5;
        border: 1px solid #e8ecf1;
        color: #1f1f1f;
      }
      .premium-modal .ant-btn-default:hover {
        background: #ebebeb;
        border-color: #d0d5dd;
      }
      .premium-modal .ant-btn-primary {
        background: linear-gradient(135deg, #1677ff 0%, #4096ff 100%);
        border: none;
        color: white;
        box-shadow: 0 4px 12px rgba(22,119,255,0.3);
      }
      .premium-modal .ant-btn-primary:hover {
        background: linear-gradient(135deg, #4096ff 0%, #1677ff 100%);
        box-shadow: 0 6px 16px rgba(22,119,255,0.4);
        transform: translateY(-1px);
      }

      @media (max-width: 768px) {
        .ant-table-cell { padding: 12px 8px !important; }
        .ant-card-body { padding: 16px; }
        .premium-modal .ant-modal-content {
          border-radius: 20px;
          margin: 0 8px;
        }
        .premium-modal .ant-modal-body {
          padding: 16px;
        }
        .premium-modal .ant-modal-footer {
          padding: 12px 16px 16px;
        }
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  return (
    <div style={{
      padding: screens.xs ? "16px" : "32px 28px",
      background: "linear-gradient(145deg, #f4f7fc 0%, #e9eef5 50%, #f0f4fa 100%)",
      minHeight: "100vh",
      position: "relative",
      backgroundAttachment: "fixed",
    }}>
      <div style={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        opacity: 0.03,
        backgroundImage: "radial-gradient(circle, #1f1f1f 1px, transparent 1px)",
        backgroundSize: "24px 24px",
        pointerEvents: "none",
        zIndex: 0,
      }} />

      <div style={{ position: "relative", zIndex: 1 }}>
        {/* Metrics Row */}
        <Row gutter={[screens.xs ? 16 : 24, screens.xs ? 16 : 24]} style={{ marginBottom: screens.xs ? 20 : 32 }}>
          <Col xs={24} sm={12} lg={6}>
            <Card className="metric-card" style={{ borderRadius: 24, border: "1px solid rgba(255,255,255,0.6)", background: "rgba(255,255,255,0.85)", backdropFilter: "blur(10px)", boxShadow: "0 8px 32px rgba(0,0,0,0.06), 0 2px 8px rgba(0,0,0,0.03)", transition: "all 0.3s ease", }} bodyStyle={{ padding: screens.xs ? 16 : 22 }} hoverable>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <div>
                  <Text type="secondary" style={{ fontSize: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>Total Users</Text>
                  <div style={{ fontSize: screens.xs ? 30 : 36, fontWeight: 800, marginTop: 8, color: "#1f1f1f" }}>{metrics.totalUsers.toLocaleString()}</div>
                  <Tag color={metrics.totalUsersTrend >= 0 ? "success" : "error"} icon={metrics.totalUsersTrend >= 0 ? <ArrowUpOutlined /> : <ArrowDownOutlined />} style={{ borderRadius: 20, marginTop: 8, fontWeight: 500 }}>
                    {metrics.totalUsersTrend > 0 ? "+" : ""}{metrics.totalUsersTrend}%
                  </Tag>
                </div>
                <div style={{ width: 56, height: 56, borderRadius: 18, background: "linear-gradient(135deg, #e6f7ff 0%, #d6ebff 100%)", display: "flex", alignItems: "center", justifyContent: "center", color: "#1677ff", fontSize: 26, boxShadow: "0 4px 12px rgba(22,119,255,0.15)" }}><UserOutlined /></div>
              </div>
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card className="metric-card" style={{ borderRadius: 24, border: "1px solid rgba(255,255,255,0.6)", background: "rgba(255,255,255,0.85)", backdropFilter: "blur(10px)", boxShadow: "0 8px 32px rgba(0,0,0,0.06), 0 2px 8px rgba(0,0,0,0.03)", }} bodyStyle={{ padding: screens.xs ? 16 : 22 }} hoverable>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <div>
                  <Text type="secondary" style={{ fontSize: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>Active Projects</Text>
                  <div style={{ fontSize: screens.xs ? 30 : 36, fontWeight: 800, marginTop: 8, color: "#1f1f1f" }}>{metrics.activeProjects}</div>
                  <Tag color="success" icon={<ArrowUpOutlined />} style={{ borderRadius: 20, marginTop: 8, fontWeight: 500 }}>+{metrics.activeProjectsNew} new</Tag>
                </div>
                <div style={{ width: 56, height: 56, borderRadius: 18, background: "linear-gradient(135deg, #f6ffed 0%, #d9f7be 100%)", display: "flex", alignItems: "center", justifyContent: "center", color: "#52c41a", fontSize: 26, boxShadow: "0 4px 12px rgba(82,196,26,0.15)" }}><ProjectOutlined /></div>
              </div>
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card className="metric-card" style={{ borderRadius: 24, border: "1px solid rgba(255,255,255,0.6)", background: "rgba(255,255,255,0.85)", backdropFilter: "blur(10px)", boxShadow: "0 8px 32px rgba(0,0,0,0.06), 0 2px 8px rgba(0,0,0,0.03)", }} bodyStyle={{ padding: screens.xs ? 16 : 22 }} hoverable>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <div>
                  <Text type="secondary" style={{ fontSize: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>Open Errors</Text>
                  <div style={{ fontSize: screens.xs ? 30 : 36, fontWeight: 800, marginTop: 8, color: "#1f1f1f" }}>{metrics.openLogs}</div>
                  <Tag color="warning" style={{ borderRadius: 20, marginTop: 8, fontWeight: 500 }}>unresolved</Tag>
                </div>
                <div style={{ width: 56, height: 56, borderRadius: 18, background: "linear-gradient(135deg, #fff2e8 0%, #ffd8bf 100%)", display: "flex", alignItems: "center", justifyContent: "center", color: "#fa541c", fontSize: 26, boxShadow: "0 4px 12px rgba(250,84,28,0.15)" }}><WarningOutlined /></div>
              </div>
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card className="metric-card" style={{ borderRadius: 24, border: "1px solid rgba(255,255,255,0.6)", background: "rgba(255,255,255,0.85)", backdropFilter: "blur(10px)", boxShadow: "0 8px 32px rgba(0,0,0,0.06), 0 2px 8px rgba(0,0,0,0.03)", }} bodyStyle={{ padding: screens.xs ? 16 : 22 }} hoverable>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <div>
                  <Text type="secondary" style={{ fontSize: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>Live Sessions</Text>
                  <div style={{ fontSize: screens.xs ? 30 : 36, fontWeight: 800, marginTop: 8, color: "#1f1f1f" }}>{metrics.liveSessions}</div>
                  <Tag color="purple" style={{ borderRadius: 20, marginTop: 8, fontWeight: 500 }}>active now</Tag>
                </div>
                <div style={{ width: 56, height: 56, borderRadius: 18, background: "linear-gradient(135deg, #f9f0ff 0%, #efdbff 100%)", display: "flex", alignItems: "center", justifyContent: "center", color: "#722ed1", fontSize: 26, boxShadow: "0 4px 12px rgba(114,46,209,0.15)" }}><BugOutlined /></div>
              </div>
            </Card>
          </Col>
        </Row>

        {/* Quick Actions Row */}
        <Row gutter={[screens.xs ? 16 : 24, screens.xs ? 16 : 24]} style={{ marginBottom: screens.xs ? 20 : 32 }}>
          <Col span={24}>
            <Card
              title={<span style={{ fontSize: 17, fontWeight: 700 }}><ThunderboltOutlined style={{ marginRight: 8, color: "#faad14" }} /> Quick Actions</span>}
              style={{ borderRadius: 24, border: "1px solid rgba(255,255,255,0.5)", background: "rgba(255,255,255,0.85)", backdropFilter: "blur(12px)", boxShadow: "0 8px 32px rgba(0,0,0,0.05)", }}
              bodyStyle={{ padding: screens.xs ? "16px" : "20px 24px" }}
            >
              <Row gutter={[screens.xs ? 12 : 24, screens.xs ? 12 : 16]}>
                {quickActions.map((action, idx) => (
                  <Col xs={24} sm={12} lg={6} key={idx}>
                    <Button block className="quick-action-btn" style={{ height: "auto", padding: screens.xs ? "16px 12px" : "22px 16px", borderRadius: 18, textAlign: "left", background: action.bg, border: "none", boxShadow: "0 2px 8px rgba(0,0,0,0.04)", transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)", }}
                      onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-6px)"; e.currentTarget.style.boxShadow = "0 12px 28px rgba(0,0,0,0.12)"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.04)"; }}
                      onClick={action.onClick}
                    >
                      <Space direction="vertical" size={screens.xs ? 6 : 10} style={{ width: "100%" }}>
                        <div style={{ fontSize: screens.xs ? 26 : 30, color: action.color, transition: "transform 0.2s ease" }} className="action-icon">{action.icon}</div>
                        <div style={{ fontWeight: 700, fontSize: screens.xs ? 15 : 16, color: "#1f1f1f" }}>{action.title}</div>
                        <Text type="secondary" style={{ fontSize: 12, lineHeight: 1.5 }}>{action.description}</Text>
                      </Space>
                    </Button>
                  </Col>
                ))}
              </Row>
            </Card>
          </Col>
        </Row>

        {/* Charts Row */}
        <Row gutter={[screens.xs ? 16 : 24, screens.xs ? 16 : 24]} style={{ marginBottom: screens.xs ? 20 : 32 }}>
          <Col xs={24} lg={12}>
            <Card title={<span style={{ fontSize: 17, fontWeight: 700 }}><FileTextOutlined style={{ marginRight: 8, color: "#1677ff" }} /> Top Error Messages</span>} style={{ borderRadius: 24, border: "1px solid rgba(255,255,255,0.5)", background: "rgba(255,255,255,0.85)", backdropFilter: "blur(10px)", boxShadow: "0 8px 32px rgba(0,0,0,0.05)", height: "100%", display: "flex", flexDirection: "column" }} bodyStyle={{ padding: screens.xs ? 16 : 24, flex: 1, display: "flex", flexDirection: "column" }}>
              {topErrors.length > 0 ? <TopErrorsChart data={topErrors} /> : <Text type="secondary">No error data available</Text>}
            </Card>
          </Col>
          <Col xs={24} lg={12}>
            <Card title={<span style={{ fontSize: 17, fontWeight: 700 }}><CalendarOutlined style={{ marginRight: 8, color: "#fa541c" }} /> Error Frequency (Last 7 Days)</span>} style={{ borderRadius: 24, border: "1px solid rgba(255,255,255,0.5)", background: "rgba(255,255,255,0.85)", backdropFilter: "blur(10px)", boxShadow: "0 8px 32px rgba(0,0,0,0.05)", height: "100%", display: "flex", flexDirection: "column" }} bodyStyle={{ padding: screens.xs ? 16 : 24, flex: 1, display: "flex", flexDirection: "column" }}>
              {errorTimeline.length > 0 ? <TimelineChart timeline={errorTimeline} screens={screens} /> : <Text type="secondary">No timeline data available</Text>}
            </Card>
          </Col>
        </Row>

        {/* Recent Logs & Session Snapshot */}
        <Row gutter={[screens.xs ? 16 : 24, screens.xs ? 16 : 24]}>
          <Col xs={24} lg={16}>
            <Card title={<span style={{ fontSize: 17, fontWeight: 700 }}><WarningOutlined style={{ marginRight: 8, color: "#d4380d" }} /> Recent Error Logs</span>} style={{ borderRadius: 24, border: "1px solid rgba(255,255,255,0.5)", background: "rgba(255,255,255,0.85)", backdropFilter: "blur(10px)", boxShadow: "0 8px 32px rgba(0,0,0,0.05)", }} bodyStyle={{ padding: !screens.lg ? "0 12px" : "0 8px" }}>
              {!screens.lg ? (
                <List dataSource={recentLogs} loading={loading} renderItem={(log) => (
                  <List.Item style={{ padding: "14px 0", borderBottom: "1px solid #f0f0f0" }}>
                    <div style={{ width: "100%", overflow: "hidden" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8, flexWrap: "wrap", gap: 6 }}>
                        <Text code style={{ fontSize: 12, fontWeight: 500 }}>{log.logId}</Text>
                        {/* MODIFIED: show project name instead of raw ID */}
                        <Tag color="blue">{projectMap[log.projectId] || log.projectId}</Tag>
                      </div>
                      <div style={{ marginBottom: 6 }}>
                        <Text style={{ fontSize: 13, color: "#d4380d", wordBreak: "break-word", whiteSpace: "normal", lineHeight: 1.4 }}>
                          {log.errorMessage?.substring(0, 80) || ""}...
                        </Text>
                      </div>
                      {log.timestamp && (
                        <Text type="secondary" style={{ fontSize: 11, wordBreak: "break-word", display: "block" }}>
                          {new Date(log.timestamp).toLocaleString()}
                        </Text>
                      )}
                    </div>
                  </List.Item>
                )} />
              ) : (
                <Table columns={logColumns} dataSource={recentLogs} pagination={false} rowKey="logId" loading={loading} rowClassName="premium-row" />
              )}
            </Card>
          </Col>
          <Col xs={24} lg={8}>
            <Card title={<span style={{ fontSize: 17, fontWeight: 700 }}><VideoCameraOutlined style={{ marginRight: 8, color: "#722ed1" }} /> Session Snapshot</span>} style={{ borderRadius: 24, border: "1px solid rgba(255,255,255,0.5)", background: "rgba(255,255,255,0.85)", backdropFilter: "blur(10px)", boxShadow: "0 8px 32px rgba(0,0,0,0.05)", height: "100%", }} bodyStyle={{ padding: screens.xs ? "0 16px" : "0 20px" }}>
              <List itemLayout="horizontal" dataSource={sessionSnapshot} loading={loading} renderItem={(session, idx) => (
                <List.Item style={{ padding: screens.xs ? "14px 0" : "18px 0", borderBottom: "1px solid #f1f3f5", animation: `fadeInUp 0.4s ease ${idx * 0.05}s both`, opacity: session.status === "completed" ? 0.85 : 1, }}>
                  <List.Item.Meta
                    avatar={
                      <Badge dot={session.status === "active"} color="#52c41a" offset={[-4, 30]}>
                        <Avatar size={44} style={{ background: session.status === "active" ? "linear-gradient(135deg, #1677ff, #4096ff)" : "#8c8c8c", fontWeight: 700, fontSize: 16, boxShadow: session.status === "active" ? "0 4px 12px rgba(22,119,255,0.3)" : "none" }}>
                          {session.username?.charAt(0).toUpperCase() || "?"}
                        </Avatar>
                      </Badge>
                    }
                    title={<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
                      <Space><Text strong style={{ fontSize: 14 }}>{session.username}</Text><Tag color="blue" style={{ borderRadius: 12, fontSize: 11, fontWeight: 500 }}>{session.role}</Tag></Space>
                      <Tag color={session.status === "active" ? "processing" : "success"} icon={session.status === "active" ? <WifiOutlined /> : <CheckCircleOutlined />} style={{ borderRadius: 20, fontWeight: 500 }}>{session.status}</Tag>
                    </div>}
                    description={<Space direction="vertical" size={2} style={{ width: "100%" }}>
                      <Space split={<Divider type="vertical" />} size={4} wrap>
                        <Text type="secondary" style={{ fontSize: 12 }}>{session.project}</Text>
                        <Tooltip title={`Login: ${session.loginTime}`}><Text type="secondary" style={{ fontSize: 12 }}><ClockCircleOutlined /> {session.status === "active" ? "Active now" : (session.duration || formatDuration(session.durationSeconds))}</Text></Tooltip>
                      </Space>
                      {session.ip && <Space size={4} wrap><Text type="secondary" style={{ fontSize: 11 }}><DesktopOutlined /> {session.ip}</Text>{(session.city || session.country) && <Text type="secondary" style={{ fontSize: 11 }}><EnvironmentOutlined /> {[session.city, session.country].filter(Boolean).join(", ")}</Text>}</Space>}
                      {session.status === "active" && session.loginTime && <Badge status="processing" text={<Text style={{ fontSize: 11, color: "#52c41a", fontWeight: 500 }}>Since {formatSessionTime(session.loginTime)}</Text>} />}
                    </Space>}
                  />
                </List.Item>
              )} />
              <div style={{ padding: "18px 0", textAlign: "center" }}>
                <Button type="primary" ghost block icon={<VideoCameraOutlined />} style={{ borderRadius: 12, height: 40, fontWeight: 600 }}>
                  Monitor All Active Sessions
                </Button>
              </div>
            </Card>
          </Col>
        </Row>

        {/* -------- PREMIUM STYLED MODALS -------- */}
        {/* Add User Modal */}
        <Modal
          className="premium-modal"
          title={<><UserOutlined style={{ color: "#1677ff" }} /> Add New User</>}
          visible={isAddUserModalVisible}
          onOk={handleAddUser}
          onCancel={() => {
            setIsAddUserModalVisible(false);
            setNewUser({ username: "", email: "", role: "", projectId: "", projectName: "" });
          }}
          okText="Create User"
          cancelText="Cancel"
          width={screens.xs ? "100%" : 540}
          style={{ top: screens.xs ? 10 : 20 }}
          bodyStyle={{ padding: "24px 24px 16px" }}
          okButtonProps={{ style: { borderRadius: 12, fontWeight: 600, height: 44 } }}
          cancelButtonProps={{ style: { borderRadius: 12, fontWeight: 500, height: 44 } }}
        >
          <Form layout="vertical" size="large">
            <Form.Item label="Username" required>
              <Input
                placeholder="e.g. john_doe"
                value={newUser.username}
                onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
              />
            </Form.Item>
            <Form.Item label="Email" required>
              <Input
                placeholder="e.g. john@example.com"
                value={newUser.email}
                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
              />
            </Form.Item>
            <Form.Item label="Role" required>
              <Input
                placeholder="e.g. admin, customer"
                value={newUser.role}
                onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
              />
            </Form.Item>
            <Form.Item label="Project ID" required>
              <Select
                placeholder="Select a project"
                value={newUser.projectId || undefined}
                onChange={(value) => {
                  const selected = projects.find(p => p.project_id === value);
                  setNewUser({
                    ...newUser,
                    projectId: selected.project_id,
                    projectName: selected.project_name
                  });
                }}
              >
                {projects.map(proj => (
                  <Option key={proj.project_id} value={proj.project_id}>
                    {proj.project_id}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item label="Project Name">
              <Input
                value={newUser.projectName}
                disabled
                placeholder="Auto-filled from project selection"
              />
            </Form.Item>
          </Form>
        </Modal>

        {/* Add Project Modal */}
        <Modal
          className="premium-modal"
          title={<><ProjectOutlined style={{ color: "#52c41a" }} /> Add New Project</>}
          visible={isAddProjectModalVisible}
          onOk={handleAddProject}
          onCancel={() => {
            setIsAddProjectModalVisible(false);
            setNewProject({ projectName: "", status: "active", remark: "" });
          }}
          okText="Create Project"
          cancelText="Cancel"
          width={screens.xs ? "100%" : 540}
          style={{ top: screens.xs ? 10 : 20 }}
          bodyStyle={{ padding: "24px 24px 16px" }}
          okButtonProps={{ style: { borderRadius: 12, fontWeight: 600, height: 44 } }}
          cancelButtonProps={{ style: { borderRadius: 12, fontWeight: 500, height: 44 } }}
        >
          <Form layout="vertical" size="large">
            <Form.Item label="Project Name" required>
              <Input
                placeholder="e.g. Marketing Website"
                value={newProject.projectName}
                onChange={(e) => setNewProject({ ...newProject, projectName: e.target.value })}
              />
            </Form.Item>
            <Form.Item label="Status">
              <Select
                value={newProject.status}
                onChange={(value) => setNewProject({ ...newProject, status: value })}
              >
                <Option value="active">Active</Option>
                <Option value="inactive">Inactive</Option>
              </Select>
            </Form.Item>
            <Form.Item label="Remarks">
              <Input.TextArea
                placeholder="Optional notes about this project..."
                value={newProject.remark}
                onChange={(e) => setNewProject({ ...newProject, remark: e.target.value })}
                rows={4}
                style={{ borderRadius: 12 }}
              />
            </Form.Item>
          </Form>
        </Modal>
      </div>
    </div>
  );
}

export default Home;